import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, Plus, Wrench, AlertTriangle, CheckCircle2, Trash2, Edit, Flame, MessageSquare } from "lucide-react";
import { toast } from "sonner";

const ISSUE_LABELS = {
  usage: "Usure",
  damage: "Cassé / abîmé",
  breakdown: "En panne",
  cleaning: "À nettoyer",
  refill: "À remplir / vidanger",
  other: "Autre",
};

const STATUS_CONFIG = {
  pending: { label: "En attente", cls: "bg-amber-100 text-amber-800 border-amber-300" },
  in_progress: { label: "En cours", cls: "bg-blue-100 text-blue-800 border-blue-300" },
  resolved: { label: "Résolu", cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  cancelled: { label: "Annulé", cls: "bg-slate-100 text-slate-700 border-slate-300" },
};

// Catégories produit pour les demandes chantier — alignées sur les catégories `equipment`.
const PRODUCT_KIND_OPTIONS = [
  { value: "bureau",          label: "Matériel de bureau",  workerHidden: true },
  { value: "petit_materiel",  label: "Petit matériel" },
  { value: "gros_materiel",   label: "Gros matériel" },
  { value: "vehicules",       label: "Véhicules" },
  { value: "vetements",       label: "Vêtements" },
  { value: "EPI",             label: "EPI" },
  { value: "manual",          label: "Produit manuel (libre)" },
];

const KIND_TO_CATEGORY = {
  bureau: "bureau",
  petit_materiel: "petit_materiel",
  gros_materiel: "gros_materiel",
  vehicules: "vehicules",
  vetements: "vetements",
  EPI: "EPI",
};

const emptyForm = {
  employee_id: "",
  product_kind: "petit_materiel",
  product_id: "",
  product_label: "",
  issue_type: "usage",
  description: "",
  priority: "normal",
};

const FieldRequestsPage = () => {
  const [data, setData] = useState({ items: [], counts: {}, is_worker: false });
  const [employees, setEmployees] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");
  const [dialog, setDialog] = useState({ open: false });
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  // Commentaires
  const [commentsRequest, setCommentsRequest] = useState(null);
  const [newComment, setNewComment] = useState("");
  const [sendingComment, setSendingComment] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      const fr = await api.getFieldRequests();
      setData(fr.data);
      // Pour un ouvrier : pas de liste de salariés ni de matériel de bureau.
      // Pour les véhicules → filtrer côté serveur sur vehicle_usage=chantier.
      const isWorker = !!fr.data?.is_worker;
      const [emp, eq] = await Promise.all([
        isWorker ? Promise.resolve({ data: [] }) : api.getEmployees(),
        api.getEquipment(isWorker ? { vehicle_usage: "chantier" } : undefined),
      ]);
      setEmployees((emp.data || []).filter((e) => !e.archived && !e.exit_date));
      setEquipment(eq.data || []);
    } catch {
      toast.error("Erreur chargement");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchData(); }, []);

  const filteredItems = useMemo(() => {
    if (filterStatus === "all") return data.items;
    return data.items.filter((i) => i.status === filterStatus);
  }, [data.items, filterStatus]);

  const visibleKindOptions = useMemo(
    () => PRODUCT_KIND_OPTIONS.filter((o) => !(data.is_worker && o.workerHidden)),
    [data.is_worker],
  );

  const productOptions = useMemo(() => {
    const cat = KIND_TO_CATEGORY[form.product_kind];
    if (!cat) return [];
    return equipment.filter((e) => (e.category || "petit_materiel") === cat);
  }, [form.product_kind, equipment]);

  const handleCreate = async () => {
    if (!form.description.trim()) return toast.error("Description obligatoire");
    if (form.product_kind === "manual" && !form.product_label.trim()) {
      return toast.error("Indiquez le produit");
    }
    if (form.product_kind !== "manual" && !form.product_id) {
      return toast.error("Sélectionnez le produit");
    }
    // Pour un ouvrier, le backend force employee_id sur son salarié lié — on évite
    // une erreur côté UI en envoyant chaîne vide; le backend résoudra.
    if (!data.is_worker && !form.employee_id) return toast.error("Sélectionnez un salarié");
    try {
      setSaving(true);
      await api.createFieldRequest(form);
      toast.success("Demande enregistrée");
      setDialog({ open: false });
      setForm(emptyForm);
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec");
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (id, status) => {
    try {
      await api.updateFieldRequest(id, { status });
      toast.success("Statut mis à jour");
      fetchData();
    } catch {
      toast.error("Échec");
    }
  };

  const handleDelete = async (id) => {
    if (!confirm("Supprimer cette demande ?")) return;
    await api.deleteFieldRequest(id);
    toast.success("Supprimée");
    fetchData();
  };

  const openComments = (r) => {
    setCommentsRequest(r);
    setNewComment("");
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !commentsRequest) return;
    try {
      setSendingComment(true);
      const res = await api.addFieldRequestComment(commentsRequest.id, newComment.trim());
      // Optimistic : append au state local + refresh
      const next = { ...commentsRequest, comments: [...(commentsRequest.comments || []), res.data] };
      setCommentsRequest(next);
      setNewComment("");
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec");
    } finally {
      setSendingComment(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!commentsRequest) return;
    if (!confirm("Supprimer ce commentaire ?")) return;
    try {
      await api.deleteFieldRequestComment(commentsRequest.id, commentId);
      const next = {
        ...commentsRequest,
        comments: (commentsRequest.comments || []).filter((c) => c.id !== commentId),
      };
      setCommentsRequest(next);
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold flex items-center gap-2">
            <Wrench className="h-6 w-6 text-indigo-600" />Demandes équipes chantier
          </h1>
          <p className="text-sm text-muted-foreground">Réparations, vidanges, nettoyages signalés par l'équipe</p>
        </div>
        <Button onClick={() => { setForm(emptyForm); setDialog({ open: true }); }} data-testid="new-field-request-btn">
          <Plus className="h-4 w-4 mr-1" />Nouvelle demande
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Card className="border-amber-200 cursor-pointer" onClick={() => setFilterStatus("pending")}>
          <CardContent className="pt-4 flex items-center gap-3">
            <div className="h-10 w-10 bg-amber-100 rounded-sm flex items-center justify-center"><AlertTriangle className="h-5 w-5 text-amber-600" /></div>
            <div>
              <div className="text-xs text-muted-foreground">En attente</div>
              <div className="text-2xl font-bold">{data.counts?.pending || 0}</div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-blue-200 cursor-pointer" onClick={() => setFilterStatus("in_progress")}>
          <CardContent className="pt-4 flex items-center gap-3">
            <div className="h-10 w-10 bg-blue-100 rounded-sm flex items-center justify-center"><Flame className="h-5 w-5 text-blue-600" /></div>
            <div>
              <div className="text-xs text-muted-foreground">En cours</div>
              <div className="text-2xl font-bold">{data.counts?.in_progress || 0}</div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-emerald-200 cursor-pointer" onClick={() => setFilterStatus("resolved")}>
          <CardContent className="pt-4 flex items-center gap-3">
            <div className="h-10 w-10 bg-emerald-100 rounded-sm flex items-center justify-center"><CheckCircle2 className="h-5 w-5 text-emerald-600" /></div>
            <div>
              <div className="text-xs text-muted-foreground">Résolues</div>
              <div className="text-2xl font-bold">{data.counts?.resolved || 0}</div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-2">
        <Label className="text-xs text-muted-foreground">Filtrer</Label>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[180px] h-9" data-testid="field-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes</SelectItem>
            <SelectItem value="pending">En attente</SelectItem>
            <SelectItem value="in_progress">En cours</SelectItem>
            <SelectItem value="resolved">Résolues</SelectItem>
            <SelectItem value="cancelled">Annulées</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardContent className="pt-4">
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin" /></div>
          ) : filteredItems.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">Aucune demande</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Salarié</TableHead>
                  <TableHead>Produit</TableHead>
                  <TableHead>Problème</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Priorité</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredItems.map((r) => (
                  <TableRow key={r.id} data-testid={`field-request-${r.id}`}>
                    <TableCell className="font-medium">{r.employee_name}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-[10px] mr-1">
                        {r.product_kind === "vehicle" ? "Véhicule" : r.product_kind === "manual" ? "Manuel" : "Matériel"}
                      </Badge>
                      {r.product_label}
                    </TableCell>
                    <TableCell className="text-sm">{ISSUE_LABELS[r.issue_type] || r.issue_type}</TableCell>
                    <TableCell className="text-xs max-w-xs truncate" title={r.description}>{r.description}</TableCell>
                    <TableCell>
                      {r.priority === "urgent"
                        ? <Badge className="bg-red-100 text-red-800 border border-red-300">Urgent</Badge>
                        : <span className="text-xs text-muted-foreground">Normal</span>}
                    </TableCell>
                    <TableCell>
                      <Select value={r.status} onValueChange={(v) => handleStatusChange(r.id, v)}>
                        <SelectTrigger className="h-8 w-[140px]" data-testid={`status-${r.id}`}>
                          <Badge variant="outline" className={STATUS_CONFIG[r.status]?.cls}>
                            {STATUS_CONFIG[r.status]?.label || r.status}
                          </Badge>
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                            <SelectItem key={k} value={k}>{v.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => openComments(r)}
                        data-testid={`fr-comments-${r.id}`}
                        title="Commentaires"
                        className="relative"
                      >
                        <MessageSquare className="h-4 w-4 text-indigo-600" />
                        {(r.comments?.length || 0) > 0 && (
                          <span className="ml-1 text-xs font-bold text-indigo-700">{r.comments.length}</span>
                        )}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleDelete(r.id)}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create dialog */}
      <Dialog open={dialog.open} onOpenChange={(v) => !v && setDialog({ open: false })}>
        <DialogContent className="sm:max-w-[600px]" data-testid="field-request-dialog">
          <DialogHeader>
            <DialogTitle>Nouvelle demande</DialogTitle>
            <DialogDescription>
              Signalez une réparation, une vidange, ou toute intervention à effectuer sur le chantier.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            {!data.is_worker && (
              <div className="col-span-2">
                <Label>Salarié *</Label>
                <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
                  <SelectTrigger data-testid="fr-employee-select"><SelectValue placeholder="Choisir un salarié" /></SelectTrigger>
                  <SelectContent>
                    {employees.map((e) => (
                      <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>Type de produit</Label>
              <Select value={form.product_kind} onValueChange={(v) => setForm({ ...form, product_kind: v, product_id: "", product_label: "" })}>
                <SelectTrigger data-testid="fr-kind-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {visibleKindOptions.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Produit *</Label>
              {form.product_kind === "manual" ? (
                <Input
                  placeholder="Saisir le produit"
                  value={form.product_label}
                  onChange={(e) => setForm({ ...form, product_label: e.target.value })}
                  data-testid="fr-product-manual"
                />
              ) : (
                <Select value={form.product_id} onValueChange={(v) => setForm({ ...form, product_id: v })}>
                  <SelectTrigger data-testid="fr-product-select"><SelectValue placeholder="Choisir" /></SelectTrigger>
                  <SelectContent>
                    {productOptions.length === 0 && <SelectItem value="_empty" disabled>— Aucun —</SelectItem>}
                    {productOptions.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.name || p.reference}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            <div>
              <Label>Problème</Label>
              <Select value={form.issue_type} onValueChange={(v) => setForm({ ...form, issue_type: v })}>
                <SelectTrigger data-testid="fr-issue-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(ISSUE_LABELS).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Priorité</Label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger data-testid="fr-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label>Description — Ce qu'il faut faire *</Label>
              <Textarea
                rows={4}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Ex: Remplacer la roulette arrière droite, vidanger l'huile du compresseur…"
                data-testid="fr-description"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialog({ open: false })} disabled={saving}>Annuler</Button>
            <Button onClick={handleCreate} disabled={saving} data-testid="fr-save-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Comments dialog */}
      <Dialog open={!!commentsRequest} onOpenChange={(v) => !v && setCommentsRequest(null)}>
        <DialogContent className="sm:max-w-[600px]" data-testid="fr-comments-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-indigo-600" />
              Commentaires
            </DialogTitle>
            <DialogDescription>
              {commentsRequest?.employee_name} — {commentsRequest?.product_label}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 max-h-[50vh] overflow-y-auto pr-1">
            {(commentsRequest?.comments || []).length === 0 ? (
              <p className="text-sm text-muted-foreground italic text-center py-6">
                Aucun commentaire pour le moment. Soyez le premier à coordonner l'intervention.
              </p>
            ) : (
              (commentsRequest?.comments || []).map((c) => (
                <div
                  key={c.id}
                  className="rounded-md border bg-slate-50 px-3 py-2"
                  data-testid={`fr-comment-${c.id}`}
                >
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <div className="text-xs">
                      <span className="font-semibold text-indigo-700">{c.author_name}</span>
                      <span className="text-muted-foreground ml-2">
                        {c.created_at ? new Date(c.created_at).toLocaleString("fr-FR") : ""}
                      </span>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDeleteComment(c.id)}
                      data-testid={`fr-comment-delete-${c.id}`}
                      title="Supprimer (auteur ou admin)"
                    >
                      <Trash2 className="h-3 w-3 text-red-500" />
                    </Button>
                  </div>
                  <p className="text-sm whitespace-pre-wrap">{c.text}</p>
                </div>
              ))
            )}
          </div>

          <div className="space-y-2 pt-2 border-t">
            <Label htmlFor="new-comment">Ajouter un commentaire</Label>
            <Textarea
              id="new-comment"
              rows={3}
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Ex : j'ai commencé l'intervention, pièce commandée, prêt pour validation…"
              data-testid="fr-comment-input"
              maxLength={2000}
            />
            <div className="text-xs text-muted-foreground text-right">
              {newComment.length} / 2000
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setCommentsRequest(null)} disabled={sendingComment}>Fermer</Button>
            <Button
              onClick={handleAddComment}
              disabled={sendingComment || !newComment.trim()}
              data-testid="fr-comment-send"
            >
              {sendingComment ? <Loader2 className="h-4 w-4 animate-spin" /> : "Publier"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FieldRequestsPage;
